// =====================================================
// REPORTE "PLAN PRODUCTIVO" DEL PRODUCTOR (BDSJEA-466)
// src/pages/Agro/Productor/ReportePlanProductivo.js
// Modal de solo lectura con el formato del reporte del sistema viejo: título, fecha de declaración y campaña, Resumen de
// Establecimientos (Agricultura y Ganadería con totales y %, y el bloque aparte de Campos improductivos), Agricultura y
// Declaración de Stock lado a lado, y Ganadería. Lo que Ewan no tiene se indica «no disponible» o «—», sin inventar valores.
// =====================================================

import React, { useEffect, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
} from "reactstrap"

import { getReportePlanProductivo } from "api/agro"
import {
  formatearNumero,
  formatearCuit,
  formatearFecha,
} from "helpers/formatters"
import { getErrorMessage } from "helpers/error_helpers"
import {
  ND,
  NA,
  MAQUINARIA,
  destinoDe,
  ha,
  haONa,
  pct,
  ETIQUETA_PLAN_PRODUCTIVO,
  idOrigenDe,
  nivelRendimientoDe,
  nivelesRendimientoDe,
  comercializadasTexto,
  TEXTO_COMO_SE_CALCULA_RENDIMIENTO,
} from "./reportePlanProductivoComun"
import {
  imprimirReporte,
  descargarExcelPlanProductivo,
} from "./reportePlanProductivoExport"
import "./ReportePlanProductivo.css"

const Barra = ({ titulo }) => <div className="rpp-barra">{titulo}</div>

const SinDatos = () => <p className="text-muted fst-italic mb-1">sin datos</p>

// BDSJEA-468: el rótulo vive en reportePlanProductivoComun (lo comparte el Excel); se reexporta para quien lo importaba de acá.
export { ETIQUETA_PLAN_PRODUCTIVO }

const FilasDestino = ({ filas }) =>
  filas.map((f, i) => (
    <tr key={`${f.idEstablecimiento}-${f.uso}-${f.idOrigen}-${i}`}>
      <td>{idOrigenDe(f)}</td>
      <td>{ha(f.haPropias)}</td>
      <td>{ha(f.haArrendadas)}</td>
      <td>{ha(f.haTotales)}</td>
      <td>{destinoDe(f) || "-"}</td>
      <td>{f.localidad || "-"}</td>
      <td>{f.departamento || "-"}</td>
      <td>{f.provincia || "-"}</td>
      <td>{nivelRendimientoDe(f)}</td>
    </tr>
  ))

const TotalesYPorcentajes = ({ t }) => (
  <>
    <tr className="rpp-tot">
      <td>Totales</td>
      <td>{ha(t.haPropias)}</td>
      <td>{ha(t.haArrendadas)}</td>
      <td>{ha(t.haTotales)}</td>
      <td colSpan={5} />
    </tr>
    <tr className="rpp-tot">
      <td>%</td>
      <td>{pct(t.porcentajePropias)}</td>
      <td>{pct(t.porcentajeArrendadas)}</td>
      <td>{pct(t.porcentajeTotal)}</td>
      <td colSpan={5} />
    </tr>
  </>
)

const ReportePlanProductivo = ({ idProductor, isOpen, toggle }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [reporte, setReporte] = useState(null)

  useEffect(() => {
    if (!isOpen) return undefined
    let cancelado = false
    setLoading(true)
    setError(null)
    setReporte(null)
    getReportePlanProductivo(idProductor)
      .then(data => {
        if (!cancelado) setReporte(data)
      })
      .catch(e => {
        if (!cancelado)
          setError(
            getErrorMessage(e, { prefix: "No se pudo generar el reporte" }),
          )
      })
      .finally(() => {
        if (!cancelado) setLoading(false)
      })
    return () => {
      cancelado = true
    }
  }, [isOpen, idProductor])

  const r = reporte
  const t = r?.totalesEstablecimientos
  const campos = r?.camposImproductivos
  const hayResumen =
    r && (r.resumenEstablecimientos.length > 0 || (campos && campos.filas))

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      scrollable
      className="rpp-modal"
      wrapClassName="rpp-modal-wrap"
    >
      <ModalHeader toggle={toggle}>
        <i className="mdi mdi-file-chart me-2" aria-hidden="true" />
        Reporte Plan Productivo
      </ModalHeader>
      <ModalBody>
        {loading && (
          <div className="text-center py-5">
            <Spinner />
          </div>
        )}
        {error && (
          <Alert color="danger" fade={false}>
            {error}
          </Alert>
        )}
        {r && (
          <div className="rpp-reporte" data-testid="reporte-plan-productivo">
            <div className="rpp-barra" style={{ marginTop: 0 }}>
              Plan Productivo
            </div>
            <p className="small text-muted mb-2">
              {r.razonSocial} · CUIT {formatearCuit(r.cuit)}
            </p>

            <table className="rpp-cab-tabla">
              <caption className="visually-hidden">
                Fecha de declaración y campaña
              </caption>
              <tbody>
                <tr className="rpp-cab">
                  <td>Fecha de declaración</td>
                  <td title={r.fechaDeclaracionOrigen || undefined}>
                    {r.fechaDeclaracion ? (
                      formatearFecha(r.fechaDeclaracion)
                    ) : (
                      <span className="rpp-nd">{ND}</span>
                    )}
                  </td>
                </tr>
                <tr className="rpp-cab">
                  <td>Campaña</td>
                  <td>
                    {r.campanas && r.campanas.length > 0 ? (
                      r.campanas.join(", ")
                    ) : (
                      <span className="rpp-nd">{ND}</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
            {r.fechaDeclaracion && r.fechaDeclaracionOrigen && (
              <p className="small text-muted mb-0">
                Fecha de declaración: {r.fechaDeclaracionOrigen}
              </p>
            )}

            <Barra titulo="Resumen de Establecimientos" />
            {!hayResumen ? (
              <SinDatos />
            ) : (
              <table>
                <caption className="visually-hidden">
                  Resumen de Establecimientos por destino
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Id</th>
                    <th scope="col">Has Propias</th>
                    <th scope="col">Has Arrendadas</th>
                    <th scope="col">Has Totales</th>
                    <th scope="col">Destino</th>
                    <th scope="col">Localidad</th>
                    <th scope="col">Departamento</th>
                    <th scope="col">Provincia</th>
                    {/* BDSJEA-519: el encabezado dice «Rendimiento» (antes «Zona», BDSJEA-470): es el nivel de rendimiento del establecimiento */}
                    <th scope="col">Rendimiento</th>
                  </tr>
                </thead>
                <tbody>
                  <FilasDestino filas={r.resumenEstablecimientos} />
                  {t && r.resumenEstablecimientos.length > 0 && (
                    <TotalesYPorcentajes t={t} />
                  )}
                  {campos && (
                    <>
                      <FilasDestino filas={campos.filas} />
                      <TotalesYPorcentajes t={campos.totales} />
                    </>
                  )}
                </tbody>
              </table>
            )}
            {t && t.haSinTenencia > 0 && (
              <p className="small text-muted mb-0">
                {ha(t.haSinTenencia)} ha sin tenencia cargada figuran solo en
                Has Totales.
              </p>
            )}

            {/* BDSJEA-519: cómo se calcula el rendimiento (docs/valores-por-zona-agro.md, sección 5) */}
            <Alert
              color="info"
              fade={false}
              className="small mt-2 mb-0 rpp-nota-rendimiento"
              data-testid="nota-rendimiento"
            >
              <b>¿Cómo se calcula el rendimiento?</b>{" "}
              {TEXTO_COMO_SE_CALCULA_RENDIMIENTO}
            </Alert>

            <Row>
              <Col md={8}>
                <Barra titulo="Agricultura" />
                {r.agricultura.length === 0 ? (
                  <SinDatos />
                ) : (
                  <>
                    <table>
                      <caption className="visually-hidden">
                        Agricultura por cultivo
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col">Cultivo</th>
                          <th scope="col">Has Propias</th>
                          <th scope="col">Has Arrendadas</th>
                          <th scope="col">Total Has por Cultivo</th>
                          <th scope="col">Rendimiento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.agricultura.map(c => (
                          <tr key={c.cultivo}>
                            <td>{c.cultivo}</td>
                            <td>{haONa(c.haPropias)}</td>
                            <td>{haONa(c.haArrendadas)}</td>
                            <td>{ha(c.haTotales)}</td>
                            <td>{nivelesRendimientoDe(c)}</td>
                          </tr>
                        ))}
                        <tr className="rpp-tot">
                          <td>Totales</td>
                          <td>{haONa(r.agriculturaTotalHaPropias)}</td>
                          <td>{haONa(r.agriculturaTotalHaArrendadas)}</td>
                          <td>{ha(r.agriculturaTotalHa)}</td>
                          <td />
                        </tr>
                      </tbody>
                    </table>
                    <table className="rpp-clave-tabla">
                      <tbody>
                        <tr>
                          <td className="rpp-clave">Maquinaria Propia</td>
                          <td className="rpp-clave">
                            {MAQUINARIA[r.maquinariaPropia] || ND}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}
              </Col>
              <Col md={4}>
                <Barra titulo="Declaración de Stock" />
                {r.stock.length === 0 ? (
                  <SinDatos />
                ) : (
                  <table>
                    <caption className="visually-hidden">
                      Declaración de Stock
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">Cultivo</th>
                        <th scope="col">(qq)</th>
                        <th scope="col">Valoración</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.stock.map(s => (
                        <tr key={s.cultivo}>
                          <td>{s.cultivo}</td>
                          <td>{ha(s.cantidadQq)}</td>
                          <td>{formatearNumero(s.valuacionUsd, 0)}</td>
                        </tr>
                      ))}
                      <tr className="rpp-tot">
                        <td>Totales</td>
                        <td>{ha(r.stockTotalQq)}</td>
                        <td>{formatearNumero(r.stockTotalValuacionUsd, 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </Col>
            </Row>

            <Barra titulo="Ganadería" />
            {r.ganaderia.length === 0 && r.ganaderiaHaTotales == null ? (
              <SinDatos />
            ) : (
              <table>
                <caption className="visually-hidden">
                  Ganadería por actividad
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Actividad</th>
                    <th scope="col">Has Propias</th>
                    <th scope="col">Has Arrendadas</th>
                    <th scope="col">Total Has</th>
                    <th scope="col">Cabezas madres</th>
                    <th scope="col">Cabezas para la venta</th>
                  </tr>
                </thead>
                <tbody>
                  {r.ganaderia.length === 0 && (
                    <tr>
                      <td className="rpp-nd">sin datos</td>
                      <td>{NA}</td>
                      <td>{NA}</td>
                      <td>{NA}</td>
                      <td>{NA}</td>
                      <td>{NA}</td>
                    </tr>
                  )}
                  {r.ganaderia.map(g => (
                    <tr key={g.actividad}>
                      <td>{g.actividad}</td>
                      <td>{haONa(g.haPropias)}</td>
                      <td>{haONa(g.haArrendadas)}</td>
                      <td>{haONa(g.haTotales)}</td>
                      <td>{ha(g.cabezasMadres)}</td>
                      <td>{ha(g.cabezasVenta)}</td>
                    </tr>
                  ))}
                  <tr className="rpp-tot">
                    <td>Totales</td>
                    <td>{haONa(r.ganaderiaHaPropias)}</td>
                    <td>{haONa(r.ganaderiaHaArrendadas)}</td>
                    <td>{haONa(r.ganaderiaHaTotales)}</td>
                    <td>{ha(r.ganaderiaTotalCabezasMadres)}</td>
                    <td>{ha(r.ganaderiaTotalCabezasVenta)}</td>
                  </tr>
                </tbody>
              </table>
            )}
            {r.ganaderiaHaNota && (
              <p className="small text-muted mb-1">{r.ganaderiaHaNota}</p>
            )}
            <table className="rpp-clave-tabla">
              <tbody>
                <tr>
                  <td className="rpp-clave">
                    Cantidad de cabezas comercializadas por año Ganado Bovino
                  </td>
                  {comercializadasTexto(r) != null ? (
                    <td className="rpp-clave">{comercializadasTexto(r)}</td>
                  ) : (
                    <td className="rpp-clave rpp-nd">{ND}</td>
                  )}
                </tr>
              </tbody>
            </table>
            <p className="mb-2">
              <b>Tambo:</b>{" "}
              {r.tambos > 0 ? `${r.tambos} registro(s)` : "sin datos"}
            </p>

            {r.noDisponible && r.noDisponible.length > 0 && (
              <Alert color="warning" fade={false} className="small">
                <b>No disponible en el sistema nuevo:</b>
                <ul className="mb-0">
                  {r.noDisponible.map(n => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          outline
          disabled={!r}
          title="Imprimir el reporte o guardarlo como PDF"
          onClick={() => imprimirReporte(r)}
        >
          <i className="mdi mdi-printer me-1" aria-hidden="true" />
          Imprimir / PDF
        </Button>
        <Button
          color="success"
          outline
          disabled={!r}
          title="Descargar el reporte en Excel (.xlsx)"
          onClick={() => descargarExcelPlanProductivo(r)}
        >
          <i className="mdi mdi-file-excel me-1" aria-hidden="true" />
          Descargar Excel
        </Button>
        <Button color="secondary" outline onClick={toggle}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ReportePlanProductivo
